<div class="min-h-screen flex flex-col lg:flex-row relative bg-gradient-to-br from-blue-50 to-white">
  <!-- Language Selector - Top Right -->
  <div class="absolute top-6 right-6 z-10">
    <app-language-selector></app-language-selector>
  </div>

  <!-- Left Side - Branding (55% on desktop, full width on mobile) -->
  <div
    class="w-full lg:w-[55%] bg-gradient-to-br from-primary/5 via-primary/10 to-blue-50 p-8 md:p-16 flex flex-col justify-start min-h-[40vh] lg:min-h-screen relative overflow-hidden"
  >
    <!-- Decorative circles -->
    <div class="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
    <div class="absolute bottom-20 right-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"></div>

    <div class="relative z-10 max-w-2xl mx-0 lg:mx-0 mt-0">
      <!-- Logo Section -->
      <div class="mb-8">
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 md:w-14 md:h-14 bg-white rounded-xl shadow-lg p-2">
            <img src="/images/bhs-logo.svg" alt="BHS Logo" class="w-full h-full object-contain" />
          </div>
          <div>
            <h1 class="text-2xl md:text-3xl font-bold text-primary">Medisync</h1>
            <p class="text-xs text-gray-600">By Better Health Solutions</p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="space-y-6 text-center lg:text-left">
        <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
          {{ translationService.translate('login.title') }}
        </h2>
        <p class="text-xl md:text-2xl text-primary font-medium">By Better Health Solutions</p>
        <p class="text-base md:text-lg text-gray-600 leading-relaxed max-w-xl">
          {{ translationService.translate('login.subtitle') }}
        </p>
      </div>

      <!-- Stethoscope Illustration -->
      <div class="mt-12 flex justify-center lg:justify-start">
        <div class="w-[280px] h-44 md:w-[380px] md:h-56 lg:w-[450px] lg:h-64 opacity-90">
          <img
            src="/images/stethoscope.svg"
            alt="Medical Stethoscope"
            class="w-full h-full object-contain drop-shadow-xl"
          />
        </div>
      </div>

      <!-- Features -->
      <div class="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="flex items-center gap-3 bg-white/50 backdrop-blur-sm rounded-xl p-4">
          <div class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              ></path>
            </svg>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-900">
              {{ translationService.translate('login.features.secure') }}
            </p>
            <p class="text-xs text-gray-600">{{ translationService.translate('login.features.secureDesc') }}</p>
          </div>
        </div>

        <div class="flex items-center gap-3 bg-white/50 backdrop-blur-sm rounded-xl p-4">
          <div class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              ></path>
            </svg>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-900">{{ translationService.translate('login.features.fast') }}</p>
            <p class="text-xs text-gray-600">{{ translationService.translate('login.features.fastDesc') }}</p>
          </div>
        </div>

        <div class="flex items-center gap-3 bg-white/50 backdrop-blur-sm rounded-xl p-4">
          <div class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-900">
              {{ translationService.translate('login.features.available') }}
            </p>
            <p class="text-xs text-gray-600">{{ translationService.translate('login.features.availableDesc') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Right Side - Login Form (45% on desktop, full width on mobile) -->
  <div class="w-full lg:w-[45%] bg-white p-6 md:p-12 flex items-center justify-center min-h-[60vh] lg:min-h-screen">
    <div class="w-full max-w-md">
      <!-- Card Container with Enhanced Shadow -->
      <div class="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 md:p-10">
        <!-- Title Section -->
        <div class="text-center mb-8">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              ></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            {{ translationService.translate('login.hospitalLogin') }}
          </h2>
          <p class="text-sm text-gray-500">
            {{ translationService.translate('login.kindlyInput') }}
          </p>
        </div>

        <!-- Active Session Confirmation -->
        @if (showConfirmation()) {
          <div
            class="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 animate-in fade-in slide-in-from-top-2 duration-300"
          >
            <div class="flex items-start gap-3 mb-4">
              <div class="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-amber-800">Active session detected</p>
                <p class="text-xs text-amber-700 mt-1 leading-relaxed">
                  You are already signed in on another device. Continuing here will disconnect that device immediately.
                </p>
              </div>
            </div>
            <div class="flex gap-3">
              <button
                type="button"
                (click)="onConfirmTransfer()"
                [disabled]="isLoading()"
                class="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (isLoading()) {
                  <span class="flex items-center justify-center gap-2">
                    <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                    Connecting...
                  </span>
                } @else {
                  Continue here & disconnect other device
                }
              </button>
              <button
                type="button"
                (click)="onCancelTransfer()"
                [disabled]="isLoading()"
                class="flex-1 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        }

        <!-- Session Expired Alert -->
        @if (sessionExpiredMessage()) {
          <div
            class="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300"
          >
            <div class="flex items-start">
              <svg class="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="text-amber-800 text-sm font-medium">{{ sessionExpiredMessage() }}</span>
            </div>
          </div>
        }

        <!-- Error Alert -->
        @if (errorMessage()) {
          <div
            class="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300"
          >
            <div class="flex items-start">
              <svg class="w-5 h-5 text-red-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="text-red-700 text-sm font-medium">{{ errorMessage() }}</span>
            </div>
          </div>
        }

        <!-- Login Form -->
        <form [formGroup]="loginForm" (ngSubmit)="onLogin()" class="space-y-5">
          <!-- Email Field -->
          <div class="space-y-2">
            <label for="email" class="text-sm font-medium text-gray-700 block">
              {{ translationService.translate('login.email.placeholder') }}
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  ></path>
                </svg>
              </div>
              <input
                id="email"
                type="email"
                [placeholder]="translationService.translate('login.email.placeholder')"
                formControlName="email"
                [class]="
                  'w-full pl-12 pr-4 py-3.5 border rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white text-sm transition-all duration-200 ' +
                  (isFieldInvalid('email') ? 'border-red-300 focus:ring-red-100' : 'border-gray-200')
                "
              />
            </div>
            @if (isFieldInvalid('email')) {
              <p class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ getFieldError('email') }}
              </p>
            }
          </div>

          <!-- Password Field -->
          <div class="space-y-2">
            <label for="password" class="text-sm font-medium text-gray-700 block">
              {{ translationService.translate('login.password.placeholder') }}
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  ></path>
                </svg>
              </div>
              <input
                id="password"
                [type]="showPassword() ? 'text' : 'password'"
                [placeholder]="translationService.translate('login.password.placeholder')"
                formControlName="password"
                [class]="
                  'w-full pl-12 pr-12 py-3.5 border rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white text-sm transition-all duration-200 ' +
                  (isFieldInvalid('password') ? 'border-red-300 focus:ring-red-100' : 'border-gray-200')
                "
              />
              <!-- Toggle Password Visibility -->
              <button
                type="button"
                (click)="togglePasswordVisibility()"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
              >
                <svg *ngIf="!showPassword()" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  ></path>
                </svg>
                <svg *ngIf="showPassword()" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  ></path>
                </svg>
              </button>
            </div>
            @if (isFieldInvalid('password')) {
              <p class="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                {{ getFieldError('password') }}
              </p>
            }
          </div>

          <!-- Login Button -->
          <button
            type="submit"
            class="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-semibold py-3.5 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transform hover:-translate-y-0.5 mt-6"
            [disabled]="isLoading() || loginForm.invalid"
          >
            @if (isLoading()) {
              <span class="flex items-center justify-center">
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>{{ translationService.translate('login.signingIn') }}</span>
              </span>
            } @else {
              <span class="flex items-center justify-center gap-2">
                <span>{{ translationService.translate('login.loginButton') }}</span>
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  ></path>
                </svg>
              </span>
            }
          </button>
        </form>

        <!-- Forgot Password Link -->
        <div class="text-center mt-6 pt-6 border-t border-gray-100">
          <p class="text-sm text-gray-600">
            {{ translationService.translate('login.forgotPassword') }}
            <a
              href="#"
              class="text-primary hover:text-primary-dark font-semibold ml-1 inline-flex items-center gap-1 transition-colors"
              (click)="onForgotPassword(); $event.preventDefault()"
            >
              {{ translationService.translate('login.resetHere') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </a>
          </p>
        </div>
      </div>

      <!-- Bottom Note -->
      <p class="text-center text-xs text-gray-500 mt-6">
        {{ translationService.translate('login.securityNote') }} •
        <span class="text-primary font-medium">{{ translationService.translate('login.secureLogin') }}</span>
      </p>
    </div>
  </div>
</div>
